document.addEventListener('DOMContentLoaded', () => {
  const carDetailsContainer = document.getElementById('car-drawer-dialog');
  const carDrawer = document.querySelector('#car-drawer');

  if (!carDetailsContainer) return;

  const translationsElement = document.getElementById('car-drawer-translations');
  const translations = translationsElement ? JSON.parse(translationsElement.textContent) : {};
  const htmlTemplates = {
    selectorItem: '<button class="car-drawer__selector-item" data-selector-item data-value="__VALUE__"><span class="car-drawer__selector-item-text text-body">__LABEL__</span></button>',
    selectorEmptyState: '<div class="car-drawer__selector-empty"><p class="car-drawer__empty-message text-body">__MESSAGE__</p></div>'
  };

  const FIELD_ORDER = ['brand', 'model', 'version', 'generation', 'bodytype'];
  const FIELD_INDEX = FIELD_ORDER.reduce((acc, field, index) => {
    acc[field] = index;
    return acc;
  }, {});

  const state = {
    selectedVehicles: new Set(),
    vehicleData: new Map(),
    carFormData: {},
    selectionNodes: {},
    currentSelectorType: null,
    savedVehicles: [],
    isDeleting: false,
    carDataCache: null,
    validationActive: false
  };

  const storage = new VehicleStorage();
  const viewManager = new CarDrawerViewManager(carDetailsContainer);

  carDetailsContainer.addEventListener('carDrawer:afterViewChange', event => {
    const targetView = event?.detail?.to;

    if (targetView === 'add') {
      requestAnimationFrame(() => {
        const brandField = carDetailsContainer.querySelector('[data-selector-type="brand"]');
        brandField?.focus({ preventScroll: true });
      });
    } else if (targetView === 'list' && state.savedVehicles.length === 0) {
      requestAnimationFrame(() => {
        const addFirstButton = carDetailsContainer.querySelector('[data-view-actions="list"] [data-action="add-first-car"]');
        addFirstButton?.focus({ preventScroll: true });
      });
    }
  });

  const getTranslation = (path, replacements = {}) => {
    const keys = path.split('.');
    let value = translations;
    for (const key of keys) {
      value = value?.[key];
      if (!value) return '';
    }
    let result = String(value);
    Object.entries(replacements).forEach(([key, val]) => {
      result = result.replace(key, val);
    });
    return result;
  };

  const getDeleteButtonText = count => {
    return count > 0
      ? getTranslation('actions.deleteSelectedWithCount', { '__COUNT__': count })
      : getTranslation('actions.deleteSelected');
  };

  const getDeleteConfirmation = count => {
    return getTranslation('confirmations.deleteVehicles', { '__COUNT__': count });
  };

  const dataManager = {
    async loadCarData() {
      if (state.carDataCache) return state.carDataCache;

      let globalData = window?.MaxtonVehicleData;

      if ((!globalData || !Array.isArray(globalData.nodes)) && window?.MaxtonVehicleDataPromise) {
        globalData = await window.MaxtonVehicleDataPromise;
      }

      if (!globalData || !Array.isArray(globalData.nodes)) {
        throw new Error('Vehicle dataset is not available.');
      }

      state.carDataCache = this.prepareData(globalData);
      return state.carDataCache;
    },

    prepareData(rawData) {
      const nodes = (rawData.nodes ?? []).map(node => {
        const id = Number(node.id);
        const parentId = Number(node.parentId);
        const level = Number(node.level);
        const fallbackType = FIELD_ORDER[level - 1] ?? 'variant';

        return {
          id,
          parentId,
          level,
          type: node.type ?? fallbackType,
          label: node.label ?? '',
          isLeaf: Boolean(node.isLeaf),
          productCount: Number.isFinite(Number(node.productCount)) ? Number(node.productCount) : 0,
          link: node.link ?? ''
        };
      });

      const nodesById = new Map();
      const childrenByParent = new Map();

      nodes.forEach(node => {
        nodesById.set(node.id, node);

        if (!childrenByParent.has(node.parentId)) {
          childrenByParent.set(node.parentId, []);
        }

        childrenByParent.get(node.parentId).push(node);
      });

      childrenByParent.forEach(children => {
        children.sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
      });

      return {
        rootId: Number(rawData.rootId) || 1876,
        generatedAt: rawData.generatedAt,
        nodes,
        nodesById,
        childrenByParent
      };
    },

    getOptions(fieldType, selectionNodes, carData) {
      const data = carData ?? state.carDataCache;
      if (!data) return [];

      if (fieldType === 'brand') {
        const children = data.childrenByParent.get(data.rootId) ?? [];
        return this.formatOptions(children, null);
      }

      if (!(fieldType in FIELD_INDEX)) return [];

      if (fieldType === 'bodytype') {
        return this.getBodyTypeOptions(selectionNodes, data);
      }

      const previousField = FIELD_ORDER[FIELD_INDEX[fieldType] - 1];
      const parentNode = selectionNodes?.[previousField];
      if (!parentNode) return [];

      const children = data.childrenByParent.get(parentNode.id) ?? [];
      const typed = children.filter(child => child.type === fieldType);

      return this.formatOptions(typed.length > 0 ? typed : children, parentNode);
    },

    formatOptions(nodes, parentNode) {
      const sorted = [...nodes].sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));

      return sorted.map(node => ({
        id: node.id,
        label: node.label,
        node,
        parent: parentNode ?? null
      }));
    },

    getBodyTypeOptions(selectionNodes, carData) {
      const data = carData ?? state.carDataCache;
      if (!data) return [];

      const generationNode = selectionNodes?.generation;
      if (generationNode) {
        const children = data.childrenByParent.get(generationNode.id) ?? [];
        return children.length > 0 ? this.expandBodyTypeOptions(children, generationNode, data) : [];
      }

      const versionNode = selectionNodes?.version;
      if (!versionNode) return [];

      const versionChildren = data.childrenByParent.get(versionNode.id) ?? [];

      // If version's children are generation-type nodes, bodytype must wait for generation selection
      if (versionChildren.some(child => child.type === 'generation')) return [];

      return versionChildren.length > 0 ? this.expandBodyTypeOptions(versionChildren, versionNode, data) : [];
    },

    expandBodyTypeOptions(nodes, parentNode, carData) {
      const options = [];

      nodes.forEach(node => {
        const childNodes = carData.childrenByParent.get(node.id) ?? [];

        if (childNodes.length === 0) {
          options.push({
            id: node.id,
            label: node.label,
            node,
            parent: parentNode
          });
          return;
        }

        childNodes.forEach(child => {
          options.push({
            id: child.id,
            label: `${node.label} - ${child.label}`,
            node: child,
            parent: node
          });
        });
      });

      options.sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
      return options;
    }
  };

  const headerTextManager = {
    async updateWithTransition(container, elements, carName) {
      const targetText = carName ?? container.dataset.defaultText ?? translations.defaultCarText;
      const currentText = elements.content.textContent;
      const isContentShowing = container.classList.contains('car-text-container--content');

      if (isContentShowing && currentText === targetText) return;

      container.classList.add('car-text-container--loading');
      container.classList.remove('car-text-container--content');

      return new Promise(resolve => {
        const handleTransition = () => {
          this.updateTextContent(container, elements.content, targetText, carName);
          container.classList.remove('car-text-container--loading');
          container.classList.add('car-text-container--content');
          resolve();
        };

        container.addEventListener('transitionend', handleTransition, { once: true });
        setTimeout(handleTransition, 200);
      });
    },

    updateTextContent(container, contentElement, targetText, carName) {
      contentElement.textContent = targetText;
      container.dataset.carState = carName ? 'selected' : 'default';

      const classes = ['header__action-text--has-car', 'mobile-topbar__car--has-car'];
      classes.forEach(cls => container.classList.toggle(cls, !!carName));
    },

    updateDirectly(container, targetText, carName) {
      container.textContent = targetText;
      container.dataset.carState = carName ? 'selected' : 'default';

      const classes = ['header__action-text--has-car', 'mobile-topbar__car--has-car'];
      classes.forEach(cls => container.classList.toggle(cls, !!carName));
    },

    async updateContainer(container, loadingClass, contentClass, carName) {
      if (!container) return;

      const elements = {
        loading: container.querySelector(loadingClass),
        content: container.querySelector(contentClass)
      };

      const hasAnimationElements = elements.loading && elements.content;
      const targetText = carName ?? container.dataset.defaultText ?? translations.defaultCarText;

      return hasAnimationElements
        ? this.updateWithTransition(container, elements, carName)
        : this.updateDirectly(container, targetText, carName);
    },

    update(carName) {
      const containers = [
        { selector: '.header__action-text', loading: '.header__action-text-loading', content: '.header__action-text-content' },
        { selector: '.mobile-topbar__car', loading: '.mobile-topbar__car-loading', content: '.mobile-topbar__car-content' }
      ];

      containers.forEach(({ selector, loading, content }) => {
        const container = document.querySelector(selector);
        this.updateContainer(container, loading, content, carName);
      });
    }
  };

  const deleteButtonManager = {
    update() {
      const deleteButton = carDetailsContainer.querySelector('[data-action="delete-selected"]');
      if (!deleteButton) return;

      const hasSelections = state.selectedVehicles.size > 0;
      deleteButton.disabled = !hasSelections;
      deleteButton.classList.toggle('button--disabled', !hasSelections);

      const buttonText = deleteButton.querySelector('.button__text');
      if (!buttonText) return;

      buttonText.textContent = hasSelections
        ? getDeleteButtonText(state.selectedVehicles.size)
        : getTranslation('actions.deleteSelected');
    }
  };

  const vehicleManager = {
    getCurrentSelectedCarName() {
      const selectedRadio = carDetailsContainer.querySelector('.car-drawer__vehicle-radio:checked');
      return selectedRadio?.dataset.carName ?? null;
    },

    handleCheckboxChange(checkbox) {
      const carName = checkbox?.dataset?.carName;
      const carValue = checkbox?.value;

      if (checkbox?.checked) {
        state.selectedVehicles.add(carValue);
        state.vehicleData.set(carValue, { name: carName, element: checkbox });
      } else {
        state.selectedVehicles.delete(carValue);
        state.vehicleData.delete(carValue);
      }

      const vehicleItem = checkbox?.closest('.car-drawer__vehicle-item');
      vehicleItem?.classList.toggle('car-drawer__vehicle-item--selected', checkbox?.checked);
      const vehicleName = vehicleItem?.querySelector('.car-drawer__vehicle-name');
      if (vehicleName) {
        vehicleName.classList.toggle('text-600', checkbox?.checked);
        vehicleName.classList.toggle('text-400', !checkbox?.checked);
      }

      deleteButtonManager.update();

      document.dispatchEvent(new CustomEvent('car-checkbox-changed', {
        detail: {
          carName,
          carValue,
          checked: checkbox.checked,
          selectedCount: state.selectedVehicles.size,
          selectedVehicles: [...state.selectedVehicles]
        }
      }));
    },

    async removeSelected() {
      if (state.selectedVehicles.size === 0 || state.isDeleting) return;

      state.isDeleting = true;

      const selectedArray = [...state.selectedVehicles];
      const vehicleNames = selectedArray.map(value => state.vehicleData.get(value)?.name).filter(Boolean);

      const removeElements = selector => {
        selectedArray.forEach(value => {
          const element = carDetailsContainer.querySelector(`${selector}[value="${CSS.escape(value)}"]`);
          element?.closest('.car-drawer__vehicle-item')?.remove();
        });
      };

      removeElements('.car-drawer__vehicle-checkbox-input');
      removeElements('.car-drawer__vehicle-radio');

      const currentSelectedVehicle = await storage.loadSelected();
      let isSelectedVehicleDeleted = false;

      selectedArray.forEach(value => {
        const vehicleName = state.vehicleData.get(value)?.name;
        if (vehicleName) {
          state.savedVehicles = state.savedVehicles.filter(v => v.name !== vehicleName);
          vehicleName === currentSelectedVehicle && (isSelectedVehicleDeleted = true);
        }
      });

      state.selectedVehicles.clear();
      state.vehicleData.clear();

      if (isSelectedVehicleDeleted) {
        await storage.saveSelected(null);
        headerTextManager.update(null);
      }

      await storage.saveVehicles(state.savedVehicles);

      emptyStateManager.update();
      viewManager.updateCarCount();
      viewManager.switchTo('list');

      document.dispatchEvent(new CustomEvent('vehicles-deleted', {
        detail: {
          deletedVehicles: vehicleNames,
          deletedCount: selectedArray.length,
          remainingCount: state.savedVehicles.length
        }
      }));

      state.isDeleting = false;
    },

    clearSelections() {
      state.selectedVehicles.clear();
      state.vehicleData.clear();

      carDetailsContainer.querySelectorAll('.car-drawer__vehicle-checkbox-input:checked')
        .forEach(checkbox => {
          checkbox.checked = false;
          const vehicleItem = checkbox.closest('.car-drawer__vehicle-item');
          vehicleItem?.classList.remove('car-drawer__vehicle-item--selected');
          const vehicleName = vehicleItem?.querySelector('.car-drawer__vehicle-name');
          if (vehicleName) {
            vehicleName.classList.remove('text-600');
            vehicleName.classList.add('text-400');
          }
        });

      deleteButtonManager.update();
    },

    async loadSaved() {
      state.savedVehicles = await storage.loadVehicles();

      if (state.savedVehicles.length > 0) {
        carDetailsContainer.querySelectorAll('.car-drawer__vehicle-item').forEach(item => item.remove());

        state.savedVehicles.forEach((vehicle, index) => {
          this.addToListView(vehicle.name, index + 1);
          this.addToManageView(vehicle.name, index + 1);
        });

        emptyStateManager.update();
        viewManager.updateCarCount();
      }

      const selectedVehicleName = await storage.loadSelected();
      if (!selectedVehicleName) {
        headerTextManager.update(null);
        return;
      }

      const radioButtons = carDetailsContainer.querySelectorAll('.car-drawer__vehicle-radio');
      let foundMatch = false;

      radioButtons.forEach(radio => {
        const carName = radio?.dataset?.carName;
        if (carName === selectedVehicleName) {
          radio.checked = true;
          const vehicleItem = radio?.closest('.car-drawer__vehicle-item');
          vehicleItem?.classList.add('car-drawer__vehicle-item--selected');
          const vehicleName = vehicleItem?.querySelector('.car-drawer__vehicle-name');
          if (vehicleName) {
            vehicleName.classList.add('text-600');
            vehicleName.classList.remove('text-400');
          }
          foundMatch = true;
        }
      });

      if (foundMatch) {
        headerTextManager.update(selectedVehicleName);

        // Update show products button state based on collection link availability
        const selectedRadio = carDetailsContainer.querySelector('.car-drawer__vehicle-radio:checked');
        const selectedCarValue = selectedRadio?.value;
        const vehicle = selectedCarValue ? state.savedVehicles.find(v => v.index === parseInt(selectedCarValue)) : null;
        const hasLink = vehicle?.link && vehicle.link.trim() !== '';
        const showProductsBtn = carDetailsContainer.querySelector('.car-drawer__show-products-btn');
        if (showProductsBtn) {
          showProductsBtn.disabled = !hasLink;
          showProductsBtn.classList.toggle('button--disabled', !hasLink);
        }
      } else {
        await storage.saveSelected(null);
        headerTextManager.update(null);
      }
    },

    createVehicleItem(carName, index, type) {
      const isCheckbox = type === 'checkbox';
      const inputType = isCheckbox ? 'checkbox' : 'radio';
      const inputClass = isCheckbox ? 'car-drawer__vehicle-checkbox-input' : 'car-drawer__vehicle-radio';
      const customClass = isCheckbox ? 'car-drawer__vehicle-checkbox' : 'car-drawer__vehicle-radio-custom';
      const labelClass = isCheckbox ? 'car-drawer__vehicle-label car-drawer__vehicle-label--manage' : 'car-drawer__vehicle-label';
      const itemClass = isCheckbox ? 'car-drawer__vehicle-item car-drawer__vehicle-item--manage' : 'car-drawer__vehicle-item';
      const inputName = isCheckbox ? 'manage_vehicles' : 'selected_vehicle';

      const vehicleItem = document.createElement('div');
      vehicleItem.className = itemClass;
      vehicleItem.innerHTML = `
        <label class="${labelClass}">
          <input
            type="${inputType}"
            name="${inputName}"
            value="${index}"
            class="${inputClass} visually-hidden"
            data-car-name="${carName}"
          >
          <span class="${customClass}"></span>
          <span class="car-drawer__vehicle-name text-body">${carName}</span>
        </label>
      `;
      return vehicleItem;
    },

    addToView(viewSelector, carName, index, type) {
      const view = carDetailsContainer.querySelector(viewSelector);
      if (!view) return;

      const emptyState = view.querySelector('.car-drawer__empty-state');
      emptyState?.style.setProperty('display', 'none');

      const listClass = type === 'checkbox'
        ? '.car-drawer__vehicle-list.car-drawer__vehicle-list--manage'
        : '.car-drawer__vehicle-list';

      let listContainer = view.querySelector(listClass);
      if (!listContainer) {
        listContainer = document.createElement('div');
        listContainer.className = type === 'checkbox'
          ? 'car-drawer__vehicle-list car-drawer__vehicle-list--manage'
          : 'car-drawer__vehicle-list';
        view.insertBefore(listContainer, view.firstChild);
      }

      const vehicleItem = this.createVehicleItem(carName, index, type);
      listContainer.appendChild(vehicleItem);
    },

    addToListView(carName, index) {
      this.addToView('[data-view-content="list"]', carName, index, 'radio');
    },

    addToManageView(carName, index) {
      this.addToView('[data-view-content="manage"]', carName, index, 'checkbox');
    },

    async addToViews(carName, carData) {
      const existingRadios = carDetailsContainer.querySelectorAll('.car-drawer__vehicle-radio');
      const existingCheckboxes = carDetailsContainer.querySelectorAll('.car-drawer__vehicle-checkbox-input');
      const newIndex = Math.max(existingRadios.length, existingCheckboxes.length, 0) + 1;

      this.addToListView(carName, newIndex);
      this.addToManageView(carName, newIndex);

      // Get the most specific link from selected nodes (bodytype > generation > version > model > brand)
      const finalNode = state.selectionNodes.bodytype
        || state.selectionNodes.generation
        || state.selectionNodes.version
        || state.selectionNodes.model
        || state.selectionNodes.brand;

      state.savedVehicles.push({
        name: carName,
        data: carData,
        link: finalNode?.link || '',
        index: newIndex,
        addedAt: new Date().toISOString()
      });

      await storage.saveVehicles(state.savedVehicles);
      emptyStateManager.update();
      viewManager.updateCarCount();

      const newRadio = carDetailsContainer.querySelector(`.car-drawer__vehicle-radio[value="${newIndex}"]`);
      if (newRadio) {
        newRadio.checked = true;
        newRadio.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  };

  const selectorManager = {
    currentOptions: new Map(),

    resetSearch({ preserveInput = false } = {}) {
      const searchInput = carDetailsContainer.querySelector('[data-selector-search]');
      if (searchInput && !preserveInput) {
        searchInput.value = '';
      }

      const clearButton = carDetailsContainer.querySelector('[data-action="clear-selector-search"]');
      if (clearButton) {
        const hasValue = Boolean(searchInput?.value?.trim());
        clearButton.hidden = !hasValue;
        clearButton.style.display = hasValue ? 'flex' : 'none';
      }

      const selectorList = carDetailsContainer.querySelector('[data-selector-list]');
      if (!selectorList) return;

      selectorList.querySelectorAll('[data-selector-item]').forEach(item => item.classList.remove('hidden'));
      selectorList.querySelector('.car-drawer__selector-empty')?.remove();
    },

    async open(type) {
      state.currentSelectorType = type;

      const selectorHeaderTitle = carDetailsContainer.querySelector('[data-selector-header-title]');
      selectorHeaderTitle && (selectorHeaderTitle.textContent = getTranslation(`fields.${type}`));

      viewManager.switchTo('selector');
      this.resetSearch();
      await this.populateList(type);
    },

    async populateList(type) {
      const selectorList = carDetailsContainer.querySelector('[data-selector-list]');
      if (!selectorList) return;

      let carData;

      try {
        carData = await dataManager.loadCarData();
      } catch (error) {
        console.error('Failed to load vehicle data.', error);
        const message = getTranslation('messages.error') || 'Unable to load options.';
        const emptyStateTemplate = htmlTemplates.selectorEmptyState;
        selectorList.innerHTML = emptyStateTemplate
          .replace('__MESSAGE__', message);
        return;
      }
      const options = dataManager.getOptions(type, state.selectionNodes, carData);

      this.currentOptions = new Map(options.map(option => [String(option.id), option]));

      if (options.length === 0) {
        const emptyStateTemplate = htmlTemplates.selectorEmptyState;
        const message = getTranslation('messages.noOptions');
        selectorList.innerHTML = emptyStateTemplate
          .replace('__MESSAGE__', message);
        return;
      }

      const fragment = document.createDocumentFragment();
      const itemTemplate = htmlTemplates.selectorItem;

      options.forEach(option => {
        const itemHtml = itemTemplate
          .replace('__VALUE__', String(option.id))
          .replace('__LABEL__', option.label);

        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = itemHtml;
        const button = tempDiv.firstElementChild;

        if (!button) return;

        button.dataset.nodeId = String(option.id);
        button.dataset.optionLabel = option.label;
        button.classList.add('car-drawer-no-close');

        if (state.selectionNodes[type]?.id === option.id) {
          button.classList.add('car-drawer__selector-item--selected');
          const itemText = button.querySelector('.car-drawer__selector-item-text');
          if (itemText) {
            itemText.classList.add('text-600');
            itemText.classList.remove('text-400');
          }
        }

        fragment.appendChild(button);
      });

      selectorList.innerHTML = '';
      selectorList.appendChild(fragment);
    },

    async handleItemSelection(nodeId) {
      if (!state.currentSelectorType) return;

      const option = this.currentOptions.get(String(nodeId));
      if (!option) return;

      carDetailsContainer.querySelectorAll('.car-drawer__selector-item--selected')
        .forEach(item => {
          item.classList.remove('car-drawer__selector-item--selected');
          const itemText = item.querySelector('.car-drawer__selector-item-text');
          if (itemText) {
            itemText.classList.remove('text-600');
            itemText.classList.add('text-400');
          }
        });

      state.selectionNodes[state.currentSelectorType] = option.node;
      state.carFormData[state.currentSelectorType] = option.label;

      const fieldValue = carDetailsContainer.querySelector(`[data-field-value="${state.currentSelectorType}"]`);
      fieldValue && (fieldValue.textContent = option.label);

      const field = carDetailsContainer.querySelector(`[data-selector-type="${state.currentSelectorType}"]`);
      field?.classList.add('car-drawer__selection-field--filled');

      const selectedButton = carDetailsContainer.querySelector(
        `[data-selector-item][data-node-id="${CSS.escape(String(option.id))}"]`
      );
      if (selectedButton) {
        selectedButton.classList.add('car-drawer__selector-item--selected');
        const itemText = selectedButton.querySelector('.car-drawer__selector-item-text');
        if (itemText) {
          itemText.classList.add('text-600');
          itemText.classList.remove('text-400');
        }
      }

      // Clear validation when user makes a selection
      state.validationActive = false;

      try {
        const carData = await dataManager.loadCarData();
        formManager.updateAvailabilityAfterSelection(state.currentSelectorType, carData);
        formManager.updateValidationMessage(formManager.determineRequiredFields(carData));
      } catch (error) {
        console.error('Failed to update vehicle selection.', error);
        formManager.updateSubmitButtonState();
      }
      this.resetSearch();
      state.currentSelectorType = null;
      viewManager.switchTo('add');
    },

    handleSearch(searchTerm) {
      const trimmedSearch = (searchTerm ?? '').trim();
      const normalizedSearch = trimmedSearch.toLowerCase();
      const clearButton = carDetailsContainer.querySelector('[data-action="clear-selector-search"]');
      if (clearButton) {
        clearButton.hidden = trimmedSearch.length === 0;
        clearButton.style.display = trimmedSearch.length === 0 ? 'none' : 'flex';
      }

      const items = carDetailsContainer.querySelectorAll('[data-selector-item]');
      let visibleCount = 0;

      items.forEach(item => {
        const text = item.querySelector('.car-drawer__selector-item-text')?.textContent?.toLowerCase() ?? '';
        const isVisible = text.includes(normalizedSearch);
        item.classList.toggle('hidden', !isVisible);
        if (isVisible) visibleCount++;
      });

      const selectorList = carDetailsContainer.querySelector('[data-selector-list]');
      if (!selectorList) return;

      const emptyState = selectorList.querySelector('.car-drawer__selector-empty');

      if (visibleCount === 0 && !emptyState) {
        const emptyStateTemplate = htmlTemplates.selectorEmptyState;
        const message = getTranslation('messages.noResults');
        const emptyDiv = document.createElement('div');
        emptyDiv.innerHTML = emptyStateTemplate
          .replace('__MESSAGE__', message);
        emptyDiv.firstElementChild && selectorList.appendChild(emptyDiv.firstElementChild);
      } else if (visibleCount > 0) {
        emptyState?.remove();
      }
    }
  };

  const formManager = {
    fieldOrder: FIELD_ORDER,

    updateAvailabilityAfterSelection(type, carData) {
      const data = carData ?? state.carDataCache;
      if (!data) return;

      const selectedIndex = this.fieldOrder.indexOf(type);
      if (selectedIndex === -1) return;

      for (let i = selectedIndex + 1; i < this.fieldOrder.length; i++) {
        const fieldType = this.fieldOrder[i];
        const options = dataManager.getOptions(fieldType, state.selectionNodes, data);

        this.clearField(fieldType);

        if (options.length > 0) {
          this.enableField(fieldType);
        }
      }

      this.updateSubmitButtonState(data);
    },

    enableField(fieldType) {
      const field = carDetailsContainer.querySelector(`[data-selector-type="${fieldType}"]`);
      if (!field) return;

      field.disabled = false;
      field.classList.remove('car-drawer__selection-field--disabled');
    },

    clearField(fieldType) {
      const field = carDetailsContainer.querySelector(`[data-selector-type="${fieldType}"]`);
      const fieldValue = carDetailsContainer.querySelector(`[data-field-value="${fieldType}"]`);

      field?.classList.remove('car-drawer__selection-field--filled');
      field?.classList.remove('car-drawer__selection-field--error');

      if (fieldType !== 'brand' && field) {
        field.disabled = true;
        field.classList.add('car-drawer__selection-field--disabled');
      }

      if (fieldValue) {
        fieldValue.textContent = '';
      }

      delete state.carFormData[fieldType];
      delete state.selectionNodes[fieldType];
    },

    updateSubmitButtonState(carData) {
      const submitBtn = carDetailsContainer.querySelector('.car-drawer__submit-btn');
      if (!submitBtn) return;

      const requiredFields = this.determineRequiredFields(carData);
      const hasSelections = Object.keys(state.carFormData).length > 0;
      const isComplete = requiredFields.every(field => state.carFormData[field]);

      // Keep button always enabled - validation message shows on click
      submitBtn.disabled = false;
      this.updateValidationMessage(requiredFields);
    },

    determineRequiredFields(carData) {
      const data = carData ?? state.carDataCache;
      const baseFields = ['brand'];
      if (!data) return baseFields;

      const followUp = this.fieldOrder
        .slice(1)
        .filter(fieldType => dataManager.getOptions(fieldType, state.selectionNodes, data).length > 0);

      const required = [...baseFields, ...followUp];

      const modelNode = state.selectionNodes?.model;
      if (modelNode) {
        const versionOptions = data.childrenByParent.get(modelNode.id) ?? [];
        if (versionOptions.length > 0 && !required.includes('version')) {
          required.push('version');
        }
      }

      return required;
    },

    updateValidationMessage(requiredFields) {
      const messageElement = carDetailsContainer.querySelector('[data-validation-message]');
      if (!messageElement) return;

      const selectionFields = carDetailsContainer.querySelectorAll('.car-drawer__selection-field');
      selectionFields.forEach(field => {
        field.classList.remove('car-drawer__selection-field--error');
        field.removeAttribute('aria-invalid');
      });

      const shouldShow = state.validationActive;

      if (!shouldShow) {
        messageElement.hidden = true;
        messageElement.textContent = '';
        return;
      }

      const missingField = requiredFields.find(field => !state.carFormData[field]);

      if (!missingField) {
        messageElement.hidden = true;
        messageElement.textContent = '';
        state.validationActive = false;
        return;
      }

      const fieldElement = carDetailsContainer.querySelector(`[data-selector-type="${missingField}"]`);
      if (fieldElement) {
        fieldElement.classList.add('car-drawer__selection-field--error');
        fieldElement.setAttribute('aria-invalid', 'true');

        if (state.validationActive) {
          fieldElement.focus({ preventScroll: true });
        }
      }

      const validationKey = `validation.${missingField}`;
      const fallbackKey = `labels.select_${missingField}`;
      const message = getTranslation(validationKey) || getTranslation(fallbackKey) || getTranslation('validation.generic');

      if (message) {
        messageElement.textContent = message;
        messageElement.hidden = false;
      } else {
        messageElement.hidden = true;
        messageElement.textContent = '';
      }
    },

    reset() {
      state.carFormData = {};
      state.selectionNodes = {};
      state.validationActive = false;
      selectorManager.resetSearch();

      carDetailsContainer.querySelectorAll('[data-field-value]')
        .forEach(field => field.textContent = '');

      carDetailsContainer.querySelectorAll('.car-drawer__selection-field').forEach(field => {
        field.classList.remove('car-drawer__selection-field--filled', 'car-drawer__selection-field--error');
        field.removeAttribute('aria-invalid');

        if (field.dataset.selectorType !== 'brand') {
          field.disabled = true;
          field.classList.add('car-drawer__selection-field--disabled');
        } else {
          field.disabled = false;
          field.classList.remove('car-drawer__selection-field--disabled');
        }
      });

      const messageElement = carDetailsContainer.querySelector('[data-validation-message]');
      if (messageElement) {
        messageElement.hidden = true;
        messageElement.textContent = '';
      }

      this.updateSubmitButtonState();
    }
  };

  const emptyStateManager = {
    update() {
      const listView = carDetailsContainer.querySelector('[data-view-content="list"]');
      const manageView = carDetailsContainer.querySelector('[data-view-content="manage"]');
      if (!listView || !manageView) return;

      const hasCars = carDetailsContainer.querySelectorAll('.car-drawer__vehicle-item').length > 0;

      [listView, manageView].forEach(view => {
        const emptyState = view.querySelector('.car-drawer__empty-state');
        const vehicleList = view.querySelector('.car-drawer__vehicle-list');

        if (hasCars) {
          emptyState?.style.setProperty('display', 'none');
          vehicleList && (vehicleList.style.display = 'flex');
        } else {
          view === listView && vehicleList && (vehicleList.style.display = 'none');
          if (view === manageView) {
            emptyState?.style.setProperty('display', 'block');
            vehicleList?.style.setProperty('display', 'none');
          }
        }
      });
    }
  };

  const protectDrawerElements = () => {
    const protectedElements = carDetailsContainer.querySelectorAll(
      '.car-drawer__selection-field, [data-selector-item], [data-selector-search], .car-drawer__back-button'
    );
    protectedElements.forEach(element => element.classList.add('car-drawer-no-close'));
  };

  carDetailsContainer.addEventListener('carDrawer:afterViewChange', event => {
    const { from, to } = event.detail;

    if (to === 'add' || to === 'selector') {
      protectDrawerElements();
    }

    if (to === 'manage') {
      deleteButtonManager.update();
    }

    document.dispatchEvent(new CustomEvent('car-drawer-view-changed', {
      detail: { view: to, previousView: from }
    }));
  });

  const keyboardHandler = {
    async animateCheckboxToggle(checkbox, newState, delay) {
      return new Promise(resolve => {
        requestAnimationFrame(() => {
          setTimeout(() => {
            if (checkbox.checked === newState) return resolve();

            checkbox.checked = newState;
            vehicleManager.handleCheckboxChange(checkbox);

            const item = checkbox.closest('.car-drawer__vehicle-item');
            if (item) {
              item.classList.add('checkbox-pulse');
              item.addEventListener('animationend', () => {
                item.classList.remove('checkbox-pulse');
                resolve();
              }, { once: true });
            } else {
              resolve();
            }
          }, delay);
        });
      });
    },

    async toggleAllCheckboxes(checkboxes, newState) {
      const animations = [...checkboxes].map((checkbox, index) =>
        this.animateCheckboxToggle(checkbox, newState, index * 20)
      );

      await Promise.all(animations);
      deleteButtonManager.update();
    }
  };
  const getLocaleFromUrl = () => {
    const match = window.location.pathname.match(/^\/(pl|de|fr|en)(\/|$)/);
    return match ? match[1] : '';
  };

  const normalizeCollectionLink = (link) => {
    return link.replace(/^\/(pl|de|fr|en)(?=\/)/, '');
  };
  
  function goToCollection(collectionLink) {
    const locale = getLocaleFromUrl(); 
    const cleanLink = normalizeCollectionLink(collectionLink);
    let newUrl;
  
    if (locale) {
      newUrl = `/${locale}${cleanLink}`;
    } else {
      newUrl = cleanLink;
    }
    window.location.href = newUrl;
  }

  const actionHandlers = {
    'manage-cars': () => {
      viewManager.switchTo('manage');
      vehicleManager.clearSelections();
      document.dispatchEvent(new CustomEvent('manage-cars'));
    },

    'cancel-manage': () => {
      viewManager.switchTo('list');
      vehicleManager.clearSelections();
      document.dispatchEvent(new CustomEvent('cancel-manage'));
    },

    'delete-selected': async () => {
      if (state.selectedVehicles.size === 0 || state.isDeleting) return;

      const confirmMessage = getDeleteConfirmation(state.selectedVehicles.size);
      const confirmDelete = confirm(confirmMessage);
      if (confirmDelete) {
        await vehicleManager.removeSelected();
      }
    },

    'add-car': () => {
      formManager.reset();
      viewManager.switchTo('add');
      document.dispatchEvent(new CustomEvent('add-car', { detail: { isFirst: false } }));
    },

    'add-first-car': () => {
      formManager.reset();
      viewManager.switchTo('add');
      document.dispatchEvent(new CustomEvent('add-car', { detail: { isFirst: true } }));
    },

    'clear-selector-search': () => {
      const searchInput = carDetailsContainer.querySelector('[data-selector-search]');
      if (searchInput) {
        searchInput.value = '';
        searchInput.focus({ preventScroll: true });
      }
      selectorManager.resetSearch();
    },

    'back-from-add': () => viewManager.switchTo('list'),
    'close-add': () => {
      formManager.reset();
      viewManager.switchTo('list');
    },

    'open-selector': async (element) => {
      const selectorType = element.dataset.selectorType;
      if (!element.disabled) {
        await selectorManager.open(selectorType);
      }
    },

    'back-from-selector': () => viewManager.switchTo('add'),
    'close-selector': () => viewManager.switchTo('add'),

    'submit-car': async (element) => {
      if (element.disabled) return;

      const requiredFields = formManager.determineRequiredFields();
      const missingFields = requiredFields.filter(field => !state.carFormData[field]);
      if (missingFields.length > 0) {
        state.validationActive = true;
        formManager.updateValidationMessage(requiredFields);
        return;
      }

      const carNameParts = [state.carFormData.brand, state.carFormData.model];
      if (state.carFormData.version) carNameParts.push(state.carFormData.version);
      if (state.carFormData.generation) carNameParts.push(state.carFormData.generation);

      const carName = carNameParts.join(' ');
      await vehicleManager.addToViews(carName, state.carFormData);

      document.dispatchEvent(new CustomEvent('car-added', {
        detail: { carData: state.carFormData, carName }
      }));

      formManager.reset();
      viewManager.switchTo('list');
    },

    'show-products': () => {
      const selectedRadio = document.querySelector('#car-drawer-dialog .car-drawer__vehicle-radio:checked');
      const selectedCarValue = selectedRadio?.value;
      const selectedCar = selectedRadio?.dataset.carName;

      // Find the saved vehicle to get its collection link
      const vehicle = selectedCarValue ? state.savedVehicles.find(v => v.index === parseInt(selectedCarValue)) : null;
      const collectionLink = vehicle?.link;

      if (selectedCar) {
        const drawerContainer = document.querySelector('.car-drawer-container');
        if (drawerContainer) {
          drawerContainer.classList.add('car-drawer-container--closing');
          drawerContainer.classList.remove('car-drawer-container--open');
        }
        document.body.classList.remove('car-drawer--open');
        carDetailsContainer?.close();

        // Redirect to collection if link exists
        if (collectionLink && collectionLink.trim() !== '') {
          goToCollection(collectionLink);
        } else {
          console.warn('No collection link found for vehicle:', selectedCar);
          // Optionally: show a message to the user
        }
      }

      document.dispatchEvent(new CustomEvent('show-products', {
        detail: {
          selectedCar: selectedCar ? {
            name: selectedCar,
            value: selectedRadio.value,
            link: collectionLink || null
          } : null
        }
      }));
    }
  };

  const eventManager = {
    handleKeydown(event) {
      if (!carDetailsContainer.open) return;

      const keyActions = {
        Escape: () => {
          if (viewManager.currentView === 'manage') {
            event.preventDefault();
            viewManager.switchTo('list');
            vehicleManager.clearSelections();
          } else if (viewManager.currentView !== 'list') {
            event.preventDefault();
            viewManager.goBack();
          } else {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
            const drawerContainer = document.querySelector('.car-drawer-container');
            if (drawerContainer) {
                      drawerContainer.classList.add('car-drawer-container--closing');
              drawerContainer.classList.remove('car-drawer-container--open');
            }
            document.body.classList.remove('car-drawer--open');
            setTimeout(() => {
              if (drawerContainer) {
                drawerContainer.classList.remove('car-drawer-container--closing');
              }
              carDetailsContainer.close();
            }, 300);
          }
        },

        a: () => this.handleSelectAll(event),
        A: () => this.handleSelectAll(event),

        Delete: () => {
          if (viewManager.currentView === 'manage') {
            const checkedCount = carDetailsContainer.querySelectorAll('.car-drawer__vehicle-checkbox-input:checked').length;
            if (checkedCount > 0) {
              event.preventDefault();
              vehicleManager.removeSelected();
            }
          }
        }
      };

      keyActions[event.key]?.();
    },

    async handleSelectAll(event) {
      if (!(event.ctrlKey || event.metaKey) || viewManager.currentView !== 'manage') return;

      event.preventDefault();
      const checkboxes = carDetailsContainer.querySelectorAll('.car-drawer__vehicle-checkbox-input');
      if (checkboxes.length === 0) return;

      const allChecked = [...checkboxes].every(cb => cb.checked);
      await keyboardHandler.toggleAllCheckboxes(checkboxes, !allChecked);
    },

    async handleAction(event) {
      if (event._carDrawerActionHandled) return;

      const carDrawerContainer = event.target.closest('#car-drawer-dialog');
      if (!carDrawerContainer) return;

      const actionElement = event.target.closest('[data-action]');
      if (!actionElement || !carDrawerContainer.contains(actionElement)) return;

      const action = actionElement.dataset.action;

      event._carDrawerActionHandled = true;
      event.preventDefault();
      event.stopPropagation();

      !carDetailsContainer.open && (
        carDetailsContainer.showModal(),
        carDetailsContainer.classList.add('menu-opening')
      );

      const handler = actionHandlers[action];
      if (handler) {
        await handler(actionElement);
      } else {
        console.warn(`Unknown action: ${action}`);
      }
    },

    handleVehicleSelection(event) {
      if (!event.target.matches('#car-drawer-dialog .car-drawer__vehicle-radio')) return;

      const selectedCarName = event.target?.dataset?.carName;
      const selectedCarValue = event.target?.value;

      document.querySelectorAll('#car-drawer-dialog .car-drawer__vehicle-item--selected')
        .forEach(item => {
          item.classList.remove('car-drawer__vehicle-item--selected');
          const vehicleName = item.querySelector('.car-drawer__vehicle-name');
          if (vehicleName) {
            vehicleName.classList.remove('text-600');
            vehicleName.classList.add('text-400');
          }
        });

      const vehicleItem = event.target.closest('.car-drawer__vehicle-item');
      vehicleItem?.classList.add('car-drawer__vehicle-item--selected');
      const selectedVehicleName = vehicleItem?.querySelector('.car-drawer__vehicle-name');
      if (selectedVehicleName) {
        selectedVehicleName.classList.add('text-600');
        selectedVehicleName.classList.remove('text-400');
      }

      storage.saveSelected(selectedCarName);
      headerTextManager.update(selectedCarName);

      // Update show products button state based on collection link availability
      const vehicle = selectedCarValue ? state.savedVehicles.find(v => v.index === parseInt(selectedCarValue)) : null;
      const hasLink = vehicle?.link && vehicle.link.trim() !== '';
      const showProductsBtn = carDetailsContainer.querySelector('.car-drawer__show-products-btn');
      if (showProductsBtn) {
        showProductsBtn.disabled = !hasLink;
        showProductsBtn.classList.toggle('button--disabled', !hasLink);
      }

      document.dispatchEvent(new CustomEvent('car-selected', {
        detail: { carName: selectedCarName, element: event.target }
      }));
    },

    handleCheckboxSelection(event) {
      if (event.target.matches('#car-drawer-dialog .car-drawer__vehicle-checkbox-input')) {
        vehicleManager.handleCheckboxChange(event.target);
      }
    },

    handleSelectorItem(event) {
      const item = event.target.closest('[data-selector-item]');
      if (!item) return;

      const nodeId = item.dataset.nodeId ?? item.dataset.value;
      if (!nodeId) return;

      selectorManager.handleItemSelection(nodeId);
    },

    handleSelectorSearch(event) {
      if (event.target.matches('[data-selector-search]')) {
        selectorManager.handleSearch(event.target?.value ?? '');
      }
    },

    handleManageItemClick(event) {
      const vehicleItem = event.target.closest('.car-drawer__vehicle-item--manage');
      if (!vehicleItem) return;

      if (event.target.closest('.car-drawer__vehicle-checkbox-input, .car-drawer__vehicle-checkbox, .car-drawer__vehicle-label, button, [data-action]')) {
        return;
      }

      const checkbox = vehicleItem.querySelector('.car-drawer__vehicle-checkbox-input');
      checkbox && (
        event.preventDefault(),
        checkbox.checked = !checkbox.checked,
        vehicleManager.handleCheckboxChange(checkbox)
      );
    },

    handleProtectedClick(event) {
      const carDrawerContainer = event.target.closest('#car-drawer-dialog');
      if (!carDrawerContainer) return;

      const isProtectedElement = event.target.closest('.car-drawer-no-close, .car-drawer-action-button, button[data-action]');
      const isSelectorItem = event.target.closest('[data-selector-item]');

      if (isProtectedElement) {
        isSelectorItem ? event.preventDefault() : event.stopPropagation();

        !carDetailsContainer.open && carDetailsContainer.showModal();
      }
    },

    handleDrawerOpen(event) {
      if (event.target.closest('[data-action="open-car-drawer"]')) {
        event.preventDefault();
        !carDetailsContainer.open && carDetailsContainer.showModal();
      }
    },

    handleDrawerClose(event) {
      if (!event.target.closest('#car-drawer-dialog .menu-drawer__close-all-button')) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      const currentView = viewManager.currentView;
      if (currentView === 'add' || currentView === 'selector') {
        viewManager.switchTo('list');
      }

      if (carDetailsContainer.open) {
        const drawerContainer = document.querySelector('.car-drawer-container');
        if (drawerContainer) {
              drawerContainer.classList.add('car-drawer-container--closing');
          drawerContainer.classList.remove('car-drawer-container--open');
        }
        document.body.classList.remove('car-drawer--open');
        setTimeout(() => {
          if (drawerContainer) {
            drawerContainer.classList.remove('car-drawer-container--closing');
          }
          carDetailsContainer.close();
        }, 300);
      }
    }
  };

  function initCarDrawer() {
    const drawerDialog = document.getElementById('car-drawer-dialog');
    const drawerContainer = document.querySelector('.car-drawer-container');

    if (!drawerContainer || !drawerDialog) {
      console.error('Car drawer elements not found');
      return;
    }

    function openDrawer() {
      drawerDialog.showModal();
      drawerContainer.classList.add('car-drawer-container--open');
      document.body.classList.add('car-drawer--open');

      requestAnimationFrame(() => {
        if (state.savedVehicles.length === 0) {
          const addFirstButton = carDetailsContainer.querySelector('[data-view-actions="list"] [data-action="add-first-car"]');
          addFirstButton?.focus();
        }
      });
    }

    function closeDrawer(event) {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
      }

      drawerContainer.classList.add('car-drawer-container--closing');
      drawerContainer.classList.remove('car-drawer-container--open');
      document.body.classList.remove('car-drawer--open');

      setTimeout(() => {
        drawerContainer.classList.remove('car-drawer-container--closing');
        drawerDialog.close();
      }, 300);
    }

    document.addEventListener('click', function(e) {
      const carActionItem = e.target.closest('.header__action-item--car');

      if (carActionItem && !drawerContainer.contains(carActionItem)) {
        e.preventDefault();
        e.stopPropagation();
        openDrawer();
        return;
      }

      const mobileTopbar = e.target.closest('[data-action="open-car-drawer"]');
      if (mobileTopbar && !drawerContainer.contains(mobileTopbar)) {
        e.preventDefault();
        e.stopPropagation();
        openDrawer();
        return;
      }

      const carIcon = e.target.closest('.header__icon--car, [data-car-drawer-trigger]');
      if (carIcon && !drawerContainer.contains(carIcon)) {
        e.preventDefault();
        e.stopPropagation();
        openDrawer();
      }
    });


    drawerDialog.addEventListener('click', function(e) {
      const innerDrawer = document.getElementById('car-drawer');
      if (!innerDrawer) {
        return;
      }

      const rect = innerDrawer.getBoundingClientRect();

      const isOutside = (
        e.clientX < rect.left ||
        e.clientX > rect.right ||
        e.clientY < rect.top ||
        e.clientY > rect.bottom
      );

      if (isOutside) {
        closeDrawer(e);
      }
    });


    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && drawerContainer.classList.contains('car-drawer-container--open')) {
        closeDrawer(e);
      }
    });
  }

  const initializeDrawer = async () => {
    await vehicleManager.loadSaved();

    viewManager.switchTo('list');
    deleteButtonManager.update();
    emptyStateManager.update();

    const checkedCheckboxes = carDetailsContainer.querySelectorAll('.car-drawer__vehicle-checkbox-input:checked');
    checkedCheckboxes.forEach(checkbox => vehicleManager.handleCheckboxChange(checkbox));

    const currentSelection = vehicleManager.getCurrentSelectedCarName();

    if (state.savedVehicles.length > 0) {
      const startTime = performance.now();

      requestAnimationFrame(() => {
        const elapsed = performance.now() - startTime;
        const delay = Math.max(0, 200 - elapsed);

        setTimeout(() => headerTextManager.update(currentSelection), delay);
      });
    } else {
      setTimeout(() => {
        const currentSelection = vehicleManager.getCurrentSelectedCarName();
        headerTextManager.update(currentSelection);
      }, 800);
    }

    setTimeout(() => {
      const containers = ['.header__action-text', '.mobile-topbar__car'];
      containers.forEach(selector => {
        const container = document.querySelector(selector);
        if (container?.classList.contains('car-text-container--loading')) {
          container.classList.remove('car-text-container--loading');
          container.classList.add('car-text-container--content');
        }
      });
    }, 1000);

    document.addEventListener('customer:logout', () => storage.saveVehicles(state.savedVehicles));
    document.addEventListener('car-selected', event => headerTextManager.update(event.detail.carName));
    document.addEventListener('vehicles-deleted', () => {
      const currentSelection = vehicleManager.getCurrentSelectedCarName();
      headerTextManager.update(currentSelection);
    });
  };

  document.addEventListener('keydown', eventManager.handleKeydown.bind(eventManager));
  document.addEventListener('change', eventManager.handleVehicleSelection);
  document.addEventListener('change', eventManager.handleCheckboxSelection);
  document.addEventListener('click', eventManager.handleAction.bind(eventManager), true);
  document.addEventListener('click', eventManager.handleSelectorItem);
  document.addEventListener('input', eventManager.handleSelectorSearch);
  document.addEventListener('click', eventManager.handleManageItemClick);
  document.addEventListener('click', eventManager.handleProtectedClick, true);
  document.addEventListener('click', eventManager.handleDrawerOpen);
  document.addEventListener('click', eventManager.handleDrawerClose);

  initializeDrawer();

  document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', initCarDrawer)
    : initCarDrawer();

  requestAnimationFrame(() => {
    const carDrawerElement = carDetailsContainer.closest('menu-drawer');
    if (!carDrawerElement) return;

    const protectedSelectors = [
      '.car-drawer-action-button',
      '.car-drawer-action-button *',
      '.car-drawer__selection-field',
      '[data-selector-item]',
      '[data-selector-search]'
    ];

    const buttonContentSelectors = [
      '.car-drawer-action-button .component-button__content',
      '.car-drawer-action-button .component-button__inner',
      '.car-drawer-action-button .component-button__label-wrapper',
      '.car-drawer-action-button .component-button__label',
      '.car-drawer-action-button .component-button__icon'
    ];

    [...protectedSelectors, ...buttonContentSelectors].forEach(selector => {
      carDetailsContainer.querySelectorAll(selector).forEach(element => {
        element.classList.add('car-drawer-no-close');
      });
    });
  });
});
